<template>
  <v-container fluid>
    <v-row justify="center">
      <v-col cols="12" md="10" lg="8">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <div>
              <h2 class="text-h5">
                {{ isEditing ? t('accounts.form.editTitle') : t('accounts.form.createTitle') }}
              </h2>
              <p class="text-body-2 text-medium-emphasis">
                {{ isEditing ? t('accounts.form.editSubtitle') : t('accounts.form.createSubtitle') }}
              </p>
            </div>
            <v-btn
              variant="text"
              icon="mdi-arrow-left"
              @click="$router.push('/accounts')"
            />
          </v-card-title>

          <v-card-text>
            <!-- Loading State -->
            <div v-if="loading" class="text-center pa-4">
              <v-progress-circular indeterminate color="primary" />
              <p class="mt-2">{{ t('accounts.form.loadingAccountInfo') }}</p>
            </div>

            <v-form v-else ref="form" v-model="valid">
              <v-row>
                <!-- Basic Information -->
                <v-col cols="12">
                  <h3 class="text-h6 mb-4">{{ t('accounts.form.basicInfo') }}</h3>
                </v-col>
                
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="account.name"
                    :label="t('accounts.form.accountName')"
                    :rules="[rules.required]"
                    required
                  />
                </v-col>
                
                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="account.account_type_id"
                    :label="t('accounts.form.accountType')"
                    :items="accountTypes"
                    item-title="name"
                    item-value="id"
                    :rules="[rules.required]"
                    required
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="account.operator_id"
                    :label="t('accounts.form.operator')"
                    :items="operators"
                    item-title="name"
                    item-value="id"
                    clearable
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-tooltip location="top">
                    <template #activator="{ props }">
                      <div v-bind="props">
                        <v-autocomplete
                          v-model="account.msisdn_validator_id"
                          :label="t('accounts.form.msisdnValidator')"
                          :items="msisdnValidators"
                          :item-title="(item) => `${countryCodeMap[item.country_code] || t('accounts.form.tooltip.code', { code: item.country_code })} (${item.minlen}-${item.maxlen})`"
                          :item-value="(item) => item.id"
                          clearable
                          hide-details
                        >
                          <template #item="{ props, item }">
                            <v-list-item v-bind="props">
                              <template #prepend>
                                <v-avatar size="32" color="primary" class="text-white text-caption">
                                  {{ item.raw.country_code }}
                                </v-avatar>
                              </template>
                              <v-list-item-title class="font-weight-medium">
                                {{ countryCodeMap[item.raw.country_code] || t('accounts.form.tooltip.code', { code: item.raw.country_code }) }}
                              </v-list-item-title>
                              <v-list-item-subtitle>
                                <div class="d-flex align-center gap-2">
                                  <span class="text-caption">
                                    {{ t('accounts.form.tooltip.code', { code: item.raw.country_code, min: item.raw.minlen, max: item.raw.maxlen }) }}
                                  </span>
                                  <v-chip
                                    :color="item.raw.autocomplete === 'ADD_COUNTRY_CODE' ? 'success' : 'warning'"
                                    size="x-small"
                                    variant="tonal"
                                  >
                                    {{ item.raw.autocomplete === 'ADD_COUNTRY_CODE' ? t('accounts.form.tooltip.addCode') : t('accounts.form.tooltip.trimCode') }}
                                  </v-chip>
                                </div>
                              </v-list-item-subtitle>
                            </v-list-item>
                          </template>
                          <template #selection="{ item }">
                            <div class="d-flex align-center gap-2">
                              <v-avatar size="24" color="primary" class="text-white text-caption">
                                {{ item.raw.country_code }}
                              </v-avatar>
                              <div class="d-flex flex-column">
                                <span class="font-weight-medium text-caption">
                                  {{ countryCodeMap[item.raw.country_code] || t('accounts.form.tooltip.code', { code: item.raw.country_code }) }}
                                </span>
                                <span class="text-caption">
                                  {{ item.raw.country_code }} ({{ item.raw.minlen }}-{{ item.raw.maxlen }})
                                </span>
                              </div>
                              <v-chip
                                :color="item.raw.autocomplete === 'ADD_COUNTRY_CODE' ? 'success' : 'warning'"
                                size="x-small"
                                variant="tonal"
                              >
                                {{ item.raw.autocomplete === 'ADD_COUNTRY_CODE' ? t('accounts.form.tooltip.addCode') : t('accounts.form.tooltip.trimCode') }}
                              </v-chip>
                            </div>
                          </template>
                        </v-autocomplete>
                      </div>
                    </template>
                    <span>
                      <strong>{{ t('accounts.form.msisdnValidator') }}:</strong><br>
                      {{ t('accounts.form.tooltip.msisdnValidator') }}<br>
                      • <strong>{{ t('accounts.form.tooltip.countryCode') }}</strong> {{ t('accounts.form.tooltip.countryCode') }}<br>
                      • <strong>{{ t('accounts.form.tooltip.length') }}</strong> {{ t('accounts.form.tooltip.length') }}<br>
                      • <strong>{{ t('accounts.form.tooltip.action') }}</strong> {{ t('accounts.form.tooltip.action') }}
                    </span>
                  </v-tooltip>
                </v-col>

                <v-col cols="12" md="6">
                  <v-switch
                    v-model="account.active"
                    :label="t('accounts.form.active')"
                    color="primary"
                    :true-value="true"
                    :false-value="false"
                    hide-details
                  />
                </v-col>

                <!-- Anti-spam Configuration -->
                <v-col cols="12">
                  <h3 class="text-h6 mb-4">{{ t('accounts.form.antiSpamConfig') }}</h3>
                </v-col>

                <v-col cols="12" md="6">
                  <v-autocomplete
                    v-model="account.anti_spam_type"
                    :label="t('accounts.form.antiSpamType')"
                    :items="antiSpamTypes"
                    :rules="[rules.required]"
                    required
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="account.amount_of_impressions"
                    :label="t('accounts.form.impressionsAmount')"
                    type="number"
                    min="0"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="account.per_days"
                    :label="t('accounts.form.perDays')"
                    type="number"
                    min="0"
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>

          <v-card-actions class="pa-6">
            <v-spacer />
            <v-btn
              variant="outlined"
              @click="$router.push('/accounts')"
              :disabled="loading"
            >
              {{ t('common.cancel') }}
            </v-btn>
            <v-btn
              color="primary"
              @click="saveAccount"
              :loading="loading"
              :disabled="!valid || loading"
            >
              {{ isEditing ? t('common.update') : t('common.create') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="showSuccess"
      color="success"
      timeout="3000"
    >
      {{ successMessage }}
      <template #actions>
        <v-btn
          variant="text"
          @click="showSuccess = false"
        >
          {{ t('common.close') }}
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Error Snackbar -->
    <v-snackbar
      v-model="showError"
      color="error"
      timeout="5000"
    >
      {{ error }}
      <template #actions>
        <v-btn
          variant="text"
          @click="showError = false"
        >
          {{ t('common.close') }}
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { accountService } from '../../services/accountService'
import { operatorService } from '../../services/operatorService'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const form = ref()
const valid = ref(false)
const loading = ref(false)
const showError = ref(false)
const showSuccess = ref(false)
const error = ref('')
const successMessage = ref('')

// Data
const accountTypes = ref<any[]>([])
const operators = ref<any[]>([])
const msisdnValidators = ref<any[]>([])

const account = reactive({
  name: '',
  account_type_id: undefined as number | undefined,
  operator_id: undefined as number | undefined,
  active: true,
  anti_spam_type: 'N-DAYS' as 'N-DAYS' | 'MONTHLY',
  amount_of_impressions: undefined as number | undefined,
  per_days: undefined as number | undefined,
  msisdn_validator_id: undefined as number | undefined
})

const antiSpamTypes = computed(() => [
  { title: 'N-DAYS', value: 'N-DAYS' },
  { title: 'MONTHLY', value: 'MONTHLY' }
])

// Country code mapping for better display
const countryCodeMap: { [key: number]: string } = {
  1: 'Estados Unidos/Canadá',
  7: 'Rusia',
  20: 'Egipto',
  27: 'Sudáfrica',
  30: 'Grecia',
  31: 'Países Bajos',
  32: 'Bélgica',
  33: 'Francia',
  34: 'España',
  36: 'Hungría',
  39: 'Italia',
  40: 'Rumania',
  41: 'Suiza',
  43: 'Austria',
  44: 'Reino Unido',
  46: 'Suecia',
  47: 'Noruega',
  48: 'Polonia',
  49: 'Alemania',
  54: 'Argentina',
  55: 'Brasil',
  56: 'Chile',
  57: 'Colombia',
  58: 'Venezuela',
  60: 'Malasia',
  62: 'Indonesia',
  63: 'Filipinas',
  65: 'Singapur',
  66: 'Tailandia',
  81: 'Japón',
  82: 'Corea del Sur',
  84: 'Vietnam',
  86: 'China',
  90: 'Turquía',
  91: 'India',
  92: 'Pakistán',
  93: 'Afganistán',
  94: 'Sri Lanka',
  95: 'Myanmar',
  96: 'Malasia',
  97: 'Kazajistán',
  98: 'Irán',
  99: 'Uzbekistán',
  212: 'Marruecos',
  213: 'Argelia',
  216: 'Túnez',
  218: 'Libia',
  225: 'Costa de Marfil',
  233: 'Ghana',
  234: 'Nigeria',
  237: 'Camerún',
  249: 'Sudán',
  251: 'Etiopía',
  252: 'Somalia',
  253: 'Yibuti',
  254: 'Kenia',
  255: 'Tanzania',
  256: 'Uganda',
  257: 'Burundi',
  258: 'Mozambique',
  260: 'Zambia',
  261: 'Madagascar',
  262: 'Reunión',
  263: 'Zimbabue',
  264: 'Namibia',
  265: 'Malawi',
  266: 'Lesoto',
  267: 'Botsuana',
  268: 'Suazilandia',
  269: 'Comoras',
  290: 'Santa Elena',
  291: 'Eritrea',
  297: 'Aruba',
  298: 'Islas Feroe',
  299: 'Groenlandia',
  350: 'Gibraltar',
  351: 'Portugal',
  352: 'Luxemburgo',
  353: 'Irlanda',
  354: 'Islandia',
  355: 'Albania',
  356: 'Malta',
  357: 'Chipre',
  358: 'Finlandia',
  359: 'Bulgaria',
  370: 'Lituania',
  371: 'Letonia',
  372: 'Estonia',
  373: 'Moldavia',
  374: 'Armenia',
  375: 'Bielorrusia',
  376: 'Andorra',
  377: 'Mónaco',
  378: 'San Marino',
  379: 'Ciudad del Vaticano',
  380: 'Ucrania',
  381: 'Serbia',
  382: 'Montenegro',
  383: 'Kosovo',
  385: 'Croacia',
  386: 'Eslovenia',
  387: 'Bosnia y Herzegovina',
  389: 'Macedonia del Norte',
  420: 'República Checa',
  421: 'Eslovaquia',
  423: 'Liechtenstein',
  500: 'Islas Malvinas',
  501: 'Belice',
  502: 'Guatemala',
  503: 'El Salvador',
  504: 'Honduras',
  505: 'Nicaragua',
  506: 'Costa Rica',
  507: 'Panamá',
  508: 'San Pedro y Miquelón',
  509: 'Haití',
  590: 'Guadalupe',
  591: 'Bolivia',
  592: 'Guyana',
  593: 'Ecuador',
  594: 'Guayana Francesa',
  595: 'Paraguay',
  596: 'Martinica',
  597: 'Surinam',
  598: 'Uruguay',
  599: 'Antillas Holandesas',
  670: 'Timor Oriental',
  671: 'Guam',
  672: 'Territorio Británico del Océano Índico',
  673: 'Brunei',
  674: 'Nauru',
  675: 'Papúa Nueva Guinea',
  676: 'Tonga',
  677: 'Islas Salomón',
  678: 'Vanuatu',
  679: 'Fiyi',
  680: 'Palaos',
  681: 'Wallis y Futuna',
  682: 'Islas Cook',
  683: 'Niue',
  685: 'Samoa',
  686: 'Kiribati',
  687: 'Nueva Caledonia',
  688: 'Tuvalu',
  689: 'Polinesia Francesa',
  690: 'Tokelau',
  691: 'Micronesia',
  692: 'Islas Marshall',
  850: 'Corea del Norte',
  852: 'Hong Kong',
  853: 'Macao',
  855: 'Camboya',
  856: 'Laos',
  880: 'Bangladés',
  886: 'Taiwán',
  960: 'Maldivas',
  961: 'Líbano',
  962: 'Jordania',
  963: 'Siria',
  964: 'Irak',
  965: 'Kuwait',
  966: 'Arabia Saudita',
  967: 'Yemen',
  968: 'Omán',
  970: 'Palestina',
  971: 'Emiratos Árabes Unidos',
  972: 'Israel',
  973: 'Baréin',
  974: 'Catar',
  975: 'Bután',
  976: 'Mongolia',
  977: 'Nepal',
  992: 'Tayikistán',
  993: 'Turkmenistán',
  994: 'Azerbaiyán',
  995: 'Georgia',
  996: 'Kirguistán',
  998: 'Uzbekistán',
  1242: 'Bahamas',
  1246: 'Barbados',
  1264: 'Anguila',
  1268: 'Antigua y Barbuda',
  1284: 'Islas Vírgenes Británicas',
  1340: 'Islas Vírgenes de los Estados Unidos',
  1345: 'Islas Caimán',
  1441: 'Bermudas',
  1473: 'Granada',
  1649: 'Islas Turcas y Caicos',
  1664: 'Montserrat',
  1671: 'Guam',
  1684: 'Samoa Americana',
  1758: 'Santa Lucía',
  1767: 'Dominica',
  1784: 'San Vicente y las Granadinas',
  1787: 'Puerto Rico',
  1809: 'República Dominicana',
  1868: 'Trinidad y Tobago',
  1869: 'San Cristóbal y Nieves',
  1876: 'Jamaica',
  1877: 'Estados Unidos',
  1939: 'Puerto Rico'
}

const rules = {
  required: (v: any) => !!v || t('common.required')
}

const isEditing = computed(() => {
  return route.params.id !== undefined
})

// Helper functions
const getValidatorInfo = (validator: any) => {
  const countryName = countryCodeMap[validator.country_code] || `Código ${validator.country_code}`
  const actionText = validator.autocomplete === 'ADD_COUNTRY_CODE' ? 'Agregar código' : 'Recortar código'
  const lengthInfo = `${validator.minlen}-${validator.maxlen} dígitos`
  
  return {
    countryName,
    actionText,
    lengthInfo,
    fullDescription: `${countryName} - ${actionText} - ${lengthInfo}`
  }
}

// Methods
const loadData = async () => {
  try {
    // Load account types
    try {
      const accountTypesRes = await accountService.getAccountTypes()
      const accountTypesData = accountTypesRes
      accountTypes.value = Array.isArray(accountTypesData) ? accountTypesData : []
    } catch (err) {
      console.error('Error loading account types:', err)
      accountTypes.value = []
    }

    // Load operators
    try {
      const operatorsRes = await operatorService.getAll({ limit: -1 })
      const operatorsData = operatorsRes.data || operatorsRes
      operators.value = Array.isArray(operatorsData) ? operatorsData : []
    } catch (err) {
      console.error('Error loading operators:', err)
      operators.value = []
    }

    // Load MSISDN validators
    try {
      const msisdnValidatorsRes = await accountService.getMsisdnValidators()
      const msisdnValidatorsData = msisdnValidatorsRes
      msisdnValidators.value = Array.isArray(msisdnValidatorsData) ? msisdnValidatorsData : []
    } catch (err) {
      console.error('Error loading MSISDN validators:', err)
      msisdnValidators.value = []
    }

  } catch (err: any) {
    error.value = t('accounts.form.errorLoadingData', { message: err.response?.data?.message || err.message })
    showError.value = true
  }
}

const loadAccount = async () => {
  if (!isEditing.value) return

  loading.value = true
  try {
    const response = await accountService.getAccountById(Number(route.params.id))
    
    // Extract data from response - the service already extracts the account data
    const accountData = response

    // Ensure all fields are properly assigned
    account.name = accountData.name || ''
    account.account_type_id = accountData.account_type_id || undefined
    account.operator_id = accountData.operator_id || undefined
    account.active = Boolean(accountData.active)
    account.anti_spam_type = accountData.anti_spam_type || 'N-DAYS'
    account.amount_of_impressions = accountData.amount_of_impressions || undefined
    account.per_days = accountData.per_days || undefined
    account.msisdn_validator_id = accountData.msisdn_validator_id || undefined
    
    // Force UI update
    await nextTick()

  } catch (err: any) {
    console.error('Error loading account:', err)
    error.value = t('accounts.form.errorLoadingAccount', { message: err.response?.data?.message || err.message })
    showError.value = true
  } finally {
    loading.value = false
  }
}

const saveAccount = async () => {
  if (!form.value?.validate()) return

  loading.value = true
  try {
    // Prepare account data with proper types
    const accountData = {
      name: account.name,
      account_type_id: Number(account.account_type_id!),
      operator_id: account.operator_id ? Number(account.operator_id) : undefined,
      active: account.active ? 1 : 0, // Convert boolean to number (0 or 1)
      anti_spam_type: account.anti_spam_type,
      amount_of_impressions: account.amount_of_impressions ? Number(account.amount_of_impressions) : undefined,
      per_days: account.per_days ? Number(account.per_days) : undefined,
      msisdn_validator_id: account.msisdn_validator_id ? Number(account.msisdn_validator_id) : undefined
    } as any

    if (isEditing.value) {
      await accountService.updateAccount(Number(route.params.id), accountData)
      successMessage.value = t('accounts.form.updateSuccess')
    } else {
      await accountService.createAccount(accountData)
      successMessage.value = t('accounts.form.createSuccess')
    }

    showSuccess.value = true
    setTimeout(() => {
      router.push('/accounts')
    }, 2000)
  } catch (err: any) {
    error.value = t('accounts.form.errorSavingAccount', { message: err.response?.data?.message || err.message })
    showError.value = true
  } finally {
    loading.value = false
  }
}

const resetAccount = () => {
  Object.assign(account, {
    name: '',
    account_type_id: undefined,
    operator_id: undefined,
    active: true,
    anti_spam_type: 'N-DAYS',
    amount_of_impressions: undefined,
    per_days: undefined,
    msisdn_validator_id: undefined
  })
}

// Lifecycle
onMounted(async () => {
  try {
    // First load reference data (dropdowns)
    await loadData()
    
    // Then load account data if editing, otherwise reset form
    if (isEditing.value) {
      await loadAccount()
    } else {
      resetAccount()
    }
  } catch (error) {
    console.error('Error in onMounted:', error)
  }
})
</script>

<style scoped>
.v-card {
  border-radius: 12px;
}
</style>
